import React from "react";
import type { Session } from "@supabase/supabase-js";
import {
  clearAuthCallbackParams,
  getAuthCallbackParams,
  hasAuthCallbackParams,
  resolveAuthSessionFromLocation,
} from "../authCallback";
import { AppleIcon } from "../share/components/icons";
import { supabase } from "../supabaseClient";
import googleLogo from "./assets/google.png";
import {
  addFeatureRequestVote,
  createFeatureRequest,
  deleteFeatureRequest,
  FeatureRequest,
  FeatureRequestStatus,
  listFeatureRequests,
  removeFeatureRequestVote,
  updateFeatureRequest,
  updateFeatureRequestStatus,
} from "./api";

const STATUS_OPTIONS: Array<{
  value: FeatureRequestStatus;
  label: string;
}> = [
  { value: "requested", label: "Requested" },
  { value: "in_progress", label: "In progress" },
  { value: "deployed", label: "Deployed" },
];

const STATUS_STYLES: Record<FeatureRequestStatus, string> = {
  requested: "bg-[#f0eef2] text-[#5f5864]",
  in_progress: "bg-[#fff0dc] text-[#9b5200]",
  deployed: "bg-[#e6f5ea] text-[#28733a]",
};

let authBootstrapPromise: Promise<Session | null> | null = null;

async function bootstrapAuthSession() {
  const params = getAuthCallbackParams();
  const session = await resolveAuthSessionFromLocation();
  if (hasAuthCallbackParams(params)) clearAuthCallbackParams();
  return session;
}

function initialAuthSession() {
  if (!authBootstrapPromise) authBootstrapPromise = bootstrapAuthSession();
  return authBootstrapPromise;
}

function errorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  }).format(date);
}

function sortByVotes(requests: FeatureRequest[]) {
  return [...requests].sort(
    (left, right) =>
      right.vote_count - left.vote_count ||
      new Date(right.created_at).getTime() -
        new Date(left.created_at).getTime(),
  );
}

function Logo() {
  return (
    <a
      href="/"
      className="font-['Figtree',sans-serif] text-[22px] font-black tracking-[-0.5px] text-[#07001a] no-underline"
      aria-label="Evo home"
    >
      evo
    </a>
  );
}

function Spinner() {
  return (
    <span
      className="inline-block size-5 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-hidden="true"
    />
  );
}

type RequestDialogProps = {
  request?: FeatureRequest;
  busy: boolean;
  onClose: () => void;
  onSave: (title: string, description: string) => Promise<void>;
};

function RequestDialog({ request, busy, onClose, onSave }: RequestDialogProps) {
  const [title, setTitle] = React.useState(request?.title ?? "");
  const [description, setDescription] = React.useState(
    request?.description ?? "",
  );
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [busy, onClose]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanTitle = title.trim();
    const cleanDescription = description.trim();
    if (!cleanTitle || !cleanDescription) {
      setError("Add a title and description.");
      return;
    }
    setError("");
    try {
      await onSave(cleanTitle, cleanDescription);
    } catch (saveError) {
      setError(errorMessage(saveError));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(7,0,26,0.5)] px-4 py-8"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <section
        aria-labelledby="request-dialog-title"
        aria-modal="true"
        className="max-h-full w-full max-w-[560px] overflow-y-auto rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(7,0,26,0.24)] sm:p-8"
        role="dialog"
      >
        <div className="mb-6 flex items-start justify-between gap-6">
          <div>
            <h2
              id="request-dialog-title"
              className="m-0 font-['Figtree',sans-serif] text-[24px] font-black tracking-[-0.5px] text-[#07001a]"
            >
              {request ? "Edit request" : "Request a feature"}
            </h2>
            <p className="mb-0 mt-2 font-['Figtree',sans-serif] text-[14px] leading-6 text-[#776f7b]">
              Share the problem you want Evo to solve.
            </p>
          </div>
          <button
            aria-label="Close"
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-[#f0eef2] text-[22px] leading-none text-[#5f5864] hover:bg-[#e7e3e9] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-2 font-['Figtree',sans-serif] text-[14px] font-bold text-[#07001a]">
            Title
            <input
              autoFocus
              className="rounded-[12px] border border-[rgba(7,0,26,0.15)] bg-white px-4 py-3 font-['Figtree',sans-serif] text-[15px] font-normal text-[#07001a] outline-none transition focus:border-[rgba(7,0,26,0.55)] focus:ring-2 focus:ring-[rgba(7,0,26,0.08)]"
              disabled={busy}
              maxLength={160}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="A short, specific feature name"
              value={title}
            />
            <span className="self-end text-[12px] font-normal text-[#8b838f]">
              {title.length}/160
            </span>
          </label>

          <label className="flex flex-col gap-2 font-['Figtree',sans-serif] text-[14px] font-bold text-[#07001a]">
            Description
            <textarea
              className="min-h-[150px] resize-y rounded-[12px] border border-[rgba(7,0,26,0.15)] bg-white px-4 py-3 font-['Figtree',sans-serif] text-[15px] font-normal leading-6 text-[#07001a] outline-none transition focus:border-[rgba(7,0,26,0.55)] focus:ring-2 focus:ring-[rgba(7,0,26,0.08)]"
              disabled={busy}
              maxLength={4000}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What would this help you do?"
              value={description}
            />
            <span className="self-end text-[12px] font-normal text-[#8b838f]">
              {description.length}/4000
            </span>
          </label>

          {error && (
            <p className="m-0 rounded-[10px] bg-[#fff0ee] px-4 py-3 font-['Figtree',sans-serif] text-[13px] text-[#b22c17]" role="alert">
              {error}
            </p>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              className="cursor-pointer rounded-[12px] border border-[rgba(7,0,26,0.12)] bg-white px-5 py-3 font-['Figtree',sans-serif] text-[14px] font-bold text-[#07001a] hover:bg-[#faf9fb] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={busy}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="flex min-w-[142px] cursor-pointer items-center justify-center gap-2 rounded-[12px] border-0 bg-[#ff381a] px-5 py-3 font-['Figtree',sans-serif] text-[14px] font-bold text-white hover:bg-[#e93218] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={busy}
              type="submit"
            >
              {busy && <Spinner />}
              {request ? "Save changes" : "Submit request"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

type SignInProps = {
  authError: string;
  authBusy: boolean;
  onEmailSignIn: (email: string, password: string) => Promise<void>;
  onProviderSignIn: (provider: "google" | "apple") => Promise<void>;
};

function SignIn({
  authError,
  authBusy,
  onEmailSignIn,
  onProviderSignIn,
}: SignInProps) {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  return (
    <main className="flex flex-1 items-center justify-center px-4 py-12 sm:py-20">
      <div className="w-full max-w-[460px] rounded-[24px] border border-[rgba(7,0,26,0.06)] bg-white p-6 shadow-[0_12px_40px_rgba(7,0,26,0.08)] sm:p-9">
        <div className="mb-7 text-center">
          <h1 className="m-0 font-['Figtree',sans-serif] text-[30px] font-black tracking-[-0.8px] text-[#07001a]">
            Help shape Evo
          </h1>
          <p className="mb-0 mt-3 font-['Figtree',sans-serif] text-[15px] leading-6 text-[#776f7b]">
            Sign in with your Evo account to request features and vote on what we build next.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            className="flex cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-[rgba(7,0,26,0.12)] bg-white px-3 py-3 font-['Figtree',sans-serif] text-[14px] font-bold text-[#07001a] hover:bg-[#faf9fb] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={authBusy}
            onClick={() => onProviderSignIn("google")}
            type="button"
          >
            <img alt="" aria-hidden="true" className="size-[18px] object-contain" src={googleLogo} />
            Google
          </button>
          <button
            className="flex cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-[rgba(7,0,26,0.12)] bg-white px-3 py-3 font-['Figtree',sans-serif] text-[14px] font-bold text-[#07001a] hover:bg-[#faf9fb] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={authBusy}
            onClick={() => onProviderSignIn("apple")}
            type="button"
          >
            <AppleIcon size={18} />
            Apple
          </button>
        </div>

        <div className="my-6 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-[rgba(7,0,26,0.1)]" />
          <span className="font-['Figtree',sans-serif] text-[12px] font-semibold uppercase tracking-[0.08em] text-[#8b838f]">or</span>
          <span className="h-px flex-1 bg-[rgba(7,0,26,0.1)]" />
        </div>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void onEmailSignIn(email.trim(), password);
          }}
        >
          <label className="flex flex-col gap-2 font-['Figtree',sans-serif] text-[13px] font-bold text-[#07001a]">
            Email
            <input
              autoComplete="email"
              className="rounded-[12px] border border-[rgba(7,0,26,0.15)] px-4 py-3 font-['Figtree',sans-serif] text-[15px] font-normal outline-none focus:border-[rgba(7,0,26,0.55)] focus:ring-2 focus:ring-[rgba(7,0,26,0.08)]"
              disabled={authBusy}
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>
          <label className="flex flex-col gap-2 font-['Figtree',sans-serif] text-[13px] font-bold text-[#07001a]">
            Password
            <input
              autoComplete="current-password"
              className="rounded-[12px] border border-[rgba(7,0,26,0.15)] px-4 py-3 font-['Figtree',sans-serif] text-[15px] font-normal outline-none focus:border-[rgba(7,0,26,0.55)] focus:ring-2 focus:ring-[rgba(7,0,26,0.08)]"
              disabled={authBusy}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>

          {authError && (
            <p className="m-0 rounded-[10px] bg-[#fff0ee] px-4 py-3 font-['Figtree',sans-serif] text-[13px] text-[#b22c17]" role="alert">
              {authError}
            </p>
          )}

          <button
            className="mt-1 flex cursor-pointer items-center justify-center gap-2 rounded-[12px] border-0 bg-[#07001a] px-5 py-3 font-['Figtree',sans-serif] text-[14px] font-bold text-white hover:bg-[#211a32] disabled:cursor-not-allowed disabled:opacity-60"
            disabled={authBusy || !email.trim() || !password}
            type="submit"
          >
            {authBusy && <Spinner />}
            Sign in
          </button>
        </form>
      </div>
    </main>
  );
}

type RequestCardProps = {
  deleting: boolean;
  featureRequest: FeatureRequest;
  isAdmin: boolean;
  pending: boolean;
  onEdit: (request: FeatureRequest) => void;
  onDelete: (request: FeatureRequest) => Promise<void>;
  onStatusChange: (
    request: FeatureRequest,
    status: FeatureRequestStatus,
  ) => Promise<void>;
  onVote: (request: FeatureRequest) => Promise<void>;
};

function RequestCard({
  deleting,
  featureRequest,
  isAdmin,
  pending,
  onDelete,
  onEdit,
  onStatusChange,
  onVote,
}: RequestCardProps) {
  const status = STATUS_OPTIONS.find(
    (option) => option.value === featureRequest.status,
  );

  return (
    <article className="flex gap-4 rounded-[18px] border border-[rgba(7,0,26,0.08)] bg-white p-4 shadow-[0_3px_12px_rgba(7,0,26,0.035)] sm:gap-5 sm:p-5">
      <button
        aria-label={`${featureRequest.viewer_has_voted ? "Remove upvote from" : "Upvote"} ${featureRequest.title}`}
        aria-pressed={featureRequest.viewer_has_voted}
        className={`flex h-[62px] w-[48px] shrink-0 cursor-pointer flex-col items-center justify-center rounded-[12px] border px-1 font-['Figtree',sans-serif] transition disabled:cursor-wait disabled:opacity-60 ${
          featureRequest.viewer_has_voted
            ? "border-[rgba(255,56,26,0.3)] bg-[#fff0ee] text-[#ff381a]"
            : "border-[rgba(7,0,26,0.09)] bg-[#faf9fb] text-[#5f5864] hover:border-[rgba(255,56,26,0.25)] hover:text-[#ff381a]"
        }`}
        disabled={pending}
        onClick={() => void onVote(featureRequest)}
        type="button"
      >
        <span className="text-[15px] leading-none" aria-hidden="true">▲</span>
        <span className="mt-1 text-[14px] font-black tabular-nums">
          {featureRequest.vote_count}
        </span>
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 font-['Figtree',sans-serif] text-[11px] font-bold ${STATUS_STYLES[featureRequest.status]}`}>
            {status?.label}
          </span>
          {featureRequest.can_edit && (
            <span className="font-['Figtree',sans-serif] text-[11px] font-semibold text-[#8b838f]">
              Your request
            </span>
          )}
        </div>

        <h2 className="mb-0 mt-3 break-words font-['Figtree',sans-serif] text-[18px] font-black leading-6 tracking-[-0.3px] text-[#07001a] sm:text-[19px]">
          {featureRequest.title}
        </h2>
        <p className="mb-0 mt-2 whitespace-pre-wrap break-words font-['Figtree',sans-serif] text-[14px] leading-[1.6] text-[#5f5864]">
          {featureRequest.description}
        </p>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[rgba(7,0,26,0.06)] pt-3">
          <p className="m-0 font-['Figtree',sans-serif] text-[12px] text-[#8b838f]">
            {featureRequest.author_name || "Evo user"}
            {featureRequest.created_at && ` · ${formatDate(featureRequest.created_at)}`}
          </p>
          <div className="flex items-center gap-2">
            {featureRequest.can_edit && (
              <button
                className="cursor-pointer rounded-[9px] border-0 bg-transparent px-2.5 py-1.5 font-['Figtree',sans-serif] text-[12px] font-bold text-[#5f5864] hover:bg-[#f0eef2] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={pending}
                onClick={() => onEdit(featureRequest)}
                type="button"
              >
                Edit
              </button>
            )}
            {(isAdmin || featureRequest.can_edit) && (
              <button
                aria-busy={deleting}
                className="cursor-pointer rounded-[9px] border-0 bg-transparent px-2.5 py-1.5 font-['Figtree',sans-serif] text-[12px] font-bold text-[#b22c17] hover:bg-[#fff0ee] disabled:cursor-wait disabled:opacity-50"
                disabled={pending}
                onClick={() => void onDelete(featureRequest)}
                type="button"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            )}
            {isAdmin && (
              <label className="sr-only" htmlFor={`status-${featureRequest.id}`}>
                Status for {featureRequest.title}
              </label>
            )}
            {isAdmin && (
              <select
                className="cursor-pointer rounded-[9px] border border-[rgba(7,0,26,0.11)] bg-white px-2.5 py-1.5 font-['Figtree',sans-serif] text-[12px] font-bold text-[#5f5864] outline-none focus:border-[rgba(7,0,26,0.55)] focus:ring-2 focus:ring-[rgba(7,0,26,0.08)] disabled:cursor-wait disabled:opacity-60"
                disabled={pending}
                id={`status-${featureRequest.id}`}
                onChange={(event) =>
                  void onStatusChange(
                    featureRequest,
                    event.target.value as FeatureRequestStatus,
                  )
                }
                value={featureRequest.status}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function FeatureRequestsPage() {
  const [session, setSession] = React.useState<Session | null>(null);
  const [authLoading, setAuthLoading] = React.useState(true);
  const [authBusy, setAuthBusy] = React.useState(false);
  const [authError, setAuthError] = React.useState("");
  const [status, setStatus] =
    React.useState<FeatureRequestStatus>("requested");
  const [requests, setRequests] = React.useState<FeatureRequest[]>([]);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [mineOnly, setMineOnly] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [pendingID, setPendingID] = React.useState<string | null>(null);
  const [deletingID, setDeletingID] = React.useState<string | null>(null);
  const [dialogRequest, setDialogRequest] =
    React.useState<FeatureRequest | null | undefined>(undefined);
  const [dialogBusy, setDialogBusy] = React.useState(false);
  const loadSequence = React.useRef(0);
  const statusRef = React.useRef(status);
  statusRef.current = status;

  React.useEffect(() => {
    if (!supabase) {
      setAuthError("Evo account sign-in is not configured.");
      setAuthLoading(false);
      return;
    }

    let active = true;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (active) setSession(nextSession);
    });

    initialAuthSession()
      .then((nextSession) => {
        if (active) setSession(nextSession);
      })
      .catch((authSessionError) => {
        if (active) setAuthError(errorMessage(authSessionError));
      })
      .finally(() => {
        if (active) setAuthLoading(false);
      });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const loadRequests = React.useCallback(async () => {
    const sequence = ++loadSequence.current;
    if (!session) {
      setRequests([]);
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    setRequests([]);
    try {
      const result = await listFeatureRequests(
        session.access_token,
        statusRef.current,
      );
      if (sequence !== loadSequence.current) return;
      setRequests(sortByVotes(result.requests ?? []));
      setIsAdmin(result.is_admin);
    } catch (loadError) {
      if (sequence !== loadSequence.current) return;
      setError(errorMessage(loadError));
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [session]);

  React.useEffect(() => {
    void loadRequests();
  }, [loadRequests, status]);

  const handleEmailSignIn = async (email: string, password: string) => {
    if (!supabase) return;
    setAuthBusy(true);
    setAuthError("");
    try {
      const { data, error: signInError } =
        await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;
      setSession(data.session);
    } catch (signInError) {
      setAuthError(errorMessage(signInError));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleProviderSignIn = async (provider: "google" | "apple") => {
    if (!supabase) return;
    setAuthBusy(true);
    setAuthError("");
    try {
      const { error: providerError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/feature-requests`,
        },
      });
      if (providerError) throw providerError;
    } catch (providerError) {
      setAuthError(errorMessage(providerError));
      setAuthBusy(false);
    }
  };

  const handleSaveRequest = async (title: string, description: string) => {
    if (!session) return;
    setDialogBusy(true);
    setError("");
    try {
      if (dialogRequest) {
        await updateFeatureRequest(session.access_token, dialogRequest.id, {
          title,
          description,
        });
      } else {
        await createFeatureRequest(session.access_token, { title, description });
        if (status !== "requested") setStatus("requested");
      }
      setDialogRequest(undefined);
      if (!dialogRequest && status !== "requested") return;
      await loadRequests();
    } catch (saveError) {
      throw saveError;
    } finally {
      setDialogBusy(false);
    }
  };

  const handleVote = async (featureRequest: FeatureRequest) => {
    if (!session || pendingID) return;
    setPendingID(featureRequest.id);
    setError("");
    setRequests((current) =>
      sortByVotes(
        current.map((item) =>
          item.id === featureRequest.id
            ? {
                ...item,
                viewer_has_voted: !item.viewer_has_voted,
                vote_count: Math.max(
                  0,
                  item.vote_count + (item.viewer_has_voted ? -1 : 1),
                ),
            }
            : item,
        ),
      ),
    );
    try {
      if (featureRequest.viewer_has_voted) {
        await removeFeatureRequestVote(session.access_token, featureRequest.id);
      } else {
        await addFeatureRequestVote(session.access_token, featureRequest.id);
      }
    } catch (voteError) {
      const message = errorMessage(voteError);
      await loadRequests();
      setError(message);
    } finally {
      setPendingID(null);
    }
  };

  const handleStatusChange = async (
    featureRequest: FeatureRequest,
    nextStatus: FeatureRequestStatus,
  ) => {
    if (!session || pendingID || nextStatus === featureRequest.status) return;
    setPendingID(featureRequest.id);
    setError("");
    try {
      await updateFeatureRequestStatus(
        session.access_token,
        featureRequest.id,
        nextStatus,
      );
      await loadRequests();
    } catch (statusError) {
      setError(errorMessage(statusError));
    } finally {
      setPendingID(null);
    }
  };

  const handleDelete = async (featureRequest: FeatureRequest) => {
    if (!session || pendingID) return;
    const confirmed = window.confirm(
      `Delete “${featureRequest.title}”? This cannot be undone.`,
    );
    if (!confirmed) return;

    setPendingID(featureRequest.id);
    setDeletingID(featureRequest.id);
    setError("");
    try {
      await deleteFeatureRequest(session.access_token, featureRequest.id);
      setRequests((current) =>
        current.filter((item) => item.id !== featureRequest.id),
      );
    } catch (deleteError) {
      setError(errorMessage(deleteError));
    } finally {
      setDeletingID(null);
      setPendingID(null);
    }
  };

  const visibleRequests = mineOnly
    ? requests.filter((featureRequest) => featureRequest.can_edit)
    : requests;

  const accountLabel =
    session?.user.user_metadata?.full_name ||
    session?.user.user_metadata?.name ||
    session?.user.email ||
    "Evo account";

  return (
    <div className="min-h-screen bg-[#faf9fb] text-[#07001a]">
      <header className="border-b border-[rgba(7,0,26,0.07)] bg-[rgba(250,249,251,0.94)]">
        <div className="mx-auto flex max-w-[1040px] items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Logo />
          {session && (
            <div className="flex min-w-0 items-center gap-3">
              <span className="hidden max-w-[240px] truncate font-['Figtree',sans-serif] text-[13px] text-[#776f7b] sm:block">
                {accountLabel}
              </span>
              <button
                className="cursor-pointer rounded-[10px] border border-[rgba(7,0,26,0.1)] bg-white px-3 py-2 font-['Figtree',sans-serif] text-[12px] font-bold text-[#5f5864] hover:bg-[#f0eef2]"
                onClick={() => void supabase?.auth.signOut()}
                type="button"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      {authLoading ? (
        <main className="flex min-h-[60vh] items-center justify-center text-[#ff381a]" aria-label="Loading account">
          <Spinner />
        </main>
      ) : !session ? (
        <SignIn
          authBusy={authBusy}
          authError={authError}
          onEmailSignIn={handleEmailSignIn}
          onProviderSignIn={handleProviderSignIn}
        />
      ) : (
        <main className="mx-auto w-full max-w-[1040px] px-4 py-9 sm:px-6 sm:py-12">
          <div className="mb-8 flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-end">
            <div className="max-w-[650px]">
              <h1 className="m-0 font-['Figtree',sans-serif] text-[34px] font-black leading-tight tracking-[-1px] text-[#07001a] sm:text-[42px]">
                Feature requests
              </h1>
              <p className="mb-0 mt-3 font-['Figtree',sans-serif] text-[15px] leading-6 text-[#776f7b] sm:text-[16px]">
                Request what would make Evo better, then upvote the ideas you want us to build next.
              </p>
            </div>
            <button
              className="w-full cursor-pointer rounded-[12px] border-0 bg-[#ff381a] px-5 py-3 font-['Figtree',sans-serif] text-[14px] font-bold text-white shadow-[0_5px_14px_rgba(255,56,26,0.2)] hover:bg-[#e93218] sm:w-auto"
              onClick={() => setDialogRequest(null)}
              type="button"
            >
              Request a feature
            </button>
          </div>

          <div className="mb-5 flex flex-col justify-between gap-3 border-b border-[rgba(7,0,26,0.09)] sm:flex-row sm:items-end">
            <div className="flex overflow-x-auto" role="tablist" aria-label="Request status">
              {STATUS_OPTIONS.map((option) => (
                <button
                  aria-selected={status === option.value}
                  className={`relative shrink-0 cursor-pointer border-0 bg-transparent px-4 py-3 font-['Figtree',sans-serif] text-[13px] font-bold ${
                    status === option.value
                      ? "text-[#07001a] after:absolute after:inset-x-3 after:bottom-0 after:h-[3px] after:rounded-t-full after:bg-[#ff381a]"
                      : "text-[#8b838f] hover:text-[#5f5864]"
                  }`}
                  key={option.value}
                  onClick={() => setStatus(option.value)}
                  role="tab"
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              aria-pressed={mineOnly}
              className={`mb-2 self-start cursor-pointer rounded-full border px-3 py-1.5 font-['Figtree',sans-serif] text-[12px] font-bold sm:self-auto ${
                mineOnly
                  ? "border-[rgba(255,56,26,0.25)] bg-[#fff0ee] text-[#ff381a]"
                  : "border-[rgba(7,0,26,0.1)] bg-white text-[#5f5864] hover:bg-[#f0eef2]"
              }`}
              onClick={() => setMineOnly((current) => !current)}
              type="button"
            >
              My requests
            </button>
          </div>

          {error && (
            <div className="mb-5 flex items-start justify-between gap-4 rounded-[12px] bg-[#fff0ee] px-4 py-3 font-['Figtree',sans-serif] text-[13px] text-[#b22c17]" role="alert">
              <span>{error}</span>
              <button
                aria-label="Dismiss error"
                className="cursor-pointer border-0 bg-transparent p-0 text-[18px] leading-none text-[#b22c17]"
                onClick={() => setError("")}
                type="button"
              >
                ×
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[220px] items-center justify-center text-[#ff381a]" aria-label="Loading feature requests">
              <Spinner />
            </div>
          ) : visibleRequests.length === 0 ? (
            <div className="rounded-[18px] border border-dashed border-[rgba(7,0,26,0.14)] bg-white px-6 py-14 text-center">
              <p className="m-0 font-['Figtree',sans-serif] text-[18px] font-black text-[#07001a]">
                {mineOnly ? "No requests from you here yet" : `No ${STATUS_OPTIONS.find((option) => option.value === status)?.label.toLowerCase()} requests yet`}
              </p>
              <p className="mb-0 mt-2 font-['Figtree',sans-serif] text-[14px] text-[#776f7b]">
                {status === "requested"
                  ? "Be the first to suggest what Evo should build."
                  : "Check back as the roadmap moves forward."}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3" role="tabpanel">
              {visibleRequests.map((featureRequest) => (
                <RequestCard
                  deleting={deletingID === featureRequest.id}
                  featureRequest={featureRequest}
                  isAdmin={isAdmin}
                  key={featureRequest.id}
                  onDelete={handleDelete}
                  onEdit={(request) => setDialogRequest(request)}
                  onStatusChange={handleStatusChange}
                  onVote={handleVote}
                  pending={pendingID === featureRequest.id}
                />
              ))}
            </div>
          )}
        </main>
      )}

      {dialogRequest !== undefined && session && (
        <RequestDialog
          busy={dialogBusy}
          onClose={() => setDialogRequest(undefined)}
          onSave={handleSaveRequest}
          request={dialogRequest ?? undefined}
        />
      )}
    </div>
  );
}
